import type { MobileSection } from "../util/mobileNavigation.ts";
import {
	createMobileReturnUrl,
	parseMobileReturnState,
	resolveMobileSourceSection,
} from "../util/mobilePageContext.ts";

export type PlayerMobileSource = Extract<
	MobileSection,
	"league" | "market" | "team"
>;

export type PlayerMobileContext = {
	actionMode: "market" | "readonly" | "team";
	sourceSection: PlayerMobileSource;
};

const isPlayerMobileSource = (
	sourceSection: MobileSection | undefined,
): sourceSection is PlayerMobileSource =>
	sourceSection === "team" ||
	sourceSection === "market" ||
	sourceSection === "league";

export const resolvePlayerMobileContext = ({
	playerTid,
	search,
	sourceSection,
}: {
	playerTid: number | null | undefined;
	search?: string | URLSearchParams;
	sourceSection?: MobileSection;
}): PlayerMobileContext => {
	const returnSource =
		search !== undefined ? parseMobileReturnState(search).sourceSection : undefined;
	const resolvedSource =
		sourceSection ??
		returnSource ??
		resolveMobileSourceSection("player", {
			playerTid,
		});
	const safeSource = isPlayerMobileSource(resolvedSource)
		? resolvedSource
		: "team";

	return {
		actionMode:
			safeSource === "market"
				? "market"
				: safeSource === "league"
					? "readonly"
					: "team",
		sourceSection: safeSource,
	};
};

export type OpponentFinanceScope = "all" | "current";

export const resolveOpponentFinanceScope = ({
	currentSeason,
	playoffs,
	season,
}: {
	currentSeason: number;
	playoffs?: string;
	season: number;
}): OpponentFinanceScope =>
	season !== currentSeason && playoffs !== "playoffs" ? "all" : "current";

export const createOpponentFinanceMobileHref = ({
	financeHref,
	returnHref,
	returnLabel,
	scrollY,
}: {
	financeHref: string;
	returnHref: string;
	returnLabel: string;
	scrollY: number;
}) =>
	createMobileReturnUrl(financeHref, {
		captureCurrentReturnUrl: false,
		pageID: "roster",
		returnHref,
		returnLabel,
		scrollY,
		sourcePage: "roster",
	});

export type TeamFinancesRosterContext = {
	href: string;
	kind: "currentTeamFallback" | "scoutedRosterReturn";
};

export const resolveTeamFinancesRosterContext = ({
	fallbackHref,
	search,
}: {
	fallbackHref: string;
	search?: string | URLSearchParams;
}): TeamFinancesRosterContext => {
	const returnState =
		search === undefined ? undefined : parseMobileReturnState(search);

	if (returnState?.sourcePage === "roster" && returnState.returnHref) {
		return {
			href: returnState.returnHref,
			kind: "scoutedRosterReturn",
		};
	}

	return {
		href: fallbackHref,
		kind: "currentTeamFallback",
	};
};

export type InjuryStatus = "availableLimited" | "longTerm" | "out" | "shortTerm";

export type InjuryMobileSummary = {
	leagueInjuries: number;
	longestGamesRemaining: number;
	playingThrough: number;
	rosterImpact: "healthy" | "limited" | "major" | "minor";
	teamInjuries: number;
};

type InjurySummaryPlayer = {
	games: number;
	playingThrough?: boolean;
	stats: {
		abbrev?: string;
		tid: number;
	};
};

export const getInjuryStatus = ({
	games,
	playingThrough,
}: Pick<InjurySummaryPlayer, "games" | "playingThrough">): InjuryStatus => {
	if (playingThrough) {
		return "availableLimited";
	}
	if (games <= 0) {
		return "out";
	}
	if (games <= 3) {
		return "shortTerm";
	}
	return "longTerm";
};

export const summarizeMobileInjuries = ({
	teamAbbrev,
	injuries,
	userTid,
}: {
	injuries: InjurySummaryPlayer[];
	teamAbbrev?: string;
	userTid: number;
}): InjuryMobileSummary => {
	const teamInjuries = injuries.filter((p) =>
		teamAbbrev !== undefined
			? p.stats.abbrev === teamAbbrev
			: p.stats.tid === userTid,
	);
	const longestGamesRemaining = teamInjuries.reduce(
		(max, p) => Math.max(max, p.games),
		0,
	);
	const playingThrough = teamInjuries.filter((p) => p.playingThrough).length;

	return {
		leagueInjuries: injuries.length,
		longestGamesRemaining,
		playingThrough,
		rosterImpact:
			teamInjuries.length === 0
				? "healthy"
				: longestGamesRemaining >= 10
					? "major"
					: playingThrough > 0
						? "limited"
						: "minor",
		teamInjuries: teamInjuries.length,
	};
};

export type TeamFinanceRiskKind =
	| "clear"
	| "hardCap"
	| "luxuryTax"
	| "minimumPayroll"
	| "overCap";

export type TeamFinanceRisk = {
	kind: TeamFinanceRiskKind;
	nextAction:
		| "addSalaryOrAcceptPenalty"
		| "manageExceptions"
		| "reducePayroll"
		| "stayFlexible";
	severity: "danger" | "ok" | "warning";
};

export type TeamFinancePrimaryActionPlan =
	| "addPayroll"
	| "manageCapOptions"
	| "none"
	| "reviewContracts"
	| "reviewMarket"
	| "tradeFit";

export const getTeamFinancePrimaryActionPlan = ({
	canEditBudget,
	isOtherTeam,
	nextAction,
}: {
	canEditBudget: boolean;
	isOtherTeam: boolean;
	nextAction: TeamFinanceRisk["nextAction"];
}): TeamFinancePrimaryActionPlan => {
	if (isOtherTeam) {
		return "tradeFit";
	}

	if (!canEditBudget) {
		return "none";
	}

	switch (nextAction) {
		case "reducePayroll":
			return "reviewContracts";
		case "addSalaryOrAcceptPenalty":
			return "addPayroll";
		case "manageExceptions":
			return "manageCapOptions";
		default:
			return "reviewMarket";
	}
};

export const getTeamFinanceRisk = ({
	luxuryTaxAmount,
	minPayrollAmount,
	payroll,
	salaryCap,
	salaryCapType,
}: {
	luxuryTaxAmount: number;
	minPayrollAmount: number;
	payroll: number;
	salaryCap: number;
	salaryCapType: "hard" | "none" | "soft";
}): TeamFinanceRisk => {
	if (salaryCapType === "hard" && payroll > salaryCap) {
		return {
			kind: "hardCap",
			nextAction: "reducePayroll",
			severity: "danger",
		};
	}
	if (luxuryTaxAmount > 0) {
		return {
			kind: "luxuryTax",
			nextAction: "reducePayroll",
			severity: "warning",
		};
	}
	if (minPayrollAmount > 0) {
		return {
			kind: "minimumPayroll",
			nextAction: "addSalaryOrAcceptPenalty",
			severity: "warning",
		};
	}
	if (salaryCapType !== "none" && payroll > salaryCap) {
		return {
			kind: "overCap",
			nextAction: "manageExceptions",
			severity: "warning",
		};
	}

	return {
		kind: "clear",
		nextAction: "stayFlexible",
		severity: "ok",
	};
};
